import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { NavLink, useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import { useAppBootstrap } from "@/context/AppBootstrapContext";
import { useNavRailPreviewOptional } from "@/context/NavRailPreviewContext";
import { useHoverPrefetch } from "@/hooks/useHoverPrefetch";
import { activeNavId, useVisibleNavItems } from "./navItems";

const BUBBLE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

interface IconRailProps {
  className?: string;
}

type BlobRect = { top: number; left: number; width: number; height: number };

/**
 * Vertical counterpart to TopBar's AnimatedNavBubble — animates pill height
 * when nav items change.
 */
function AnimatedIconRailShell({
  layoutKey,
  className,
  navRef,
  onMouseLeave,
  children,
}: {
  layoutKey: string;
  className?: string;
  navRef: RefObject<HTMLElement | null>;
  onMouseLeave?: () => void;
  children: ReactNode;
}) {
  const innerRef = useRef<HTMLElement | null>(null);
  const heightRef = useRef<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [canTransition, setCanTransition] = useState(false);
  const { phase } = useAppBootstrap();

  useEffect(() => {
    if (phase !== "ready") {
      setCanTransition(false);
      return;
    }
    const frame = requestAnimationFrame(() => setCanTransition(true));
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      innerRef.current = node;
      (navRef as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    [navRef],
  );

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;

    const target = node.offsetHeight;

    if (heightRef.current === null) {
      heightRef.current = target;
      setHeight(target);
      return;
    }

    if (target === heightRef.current) return;

    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        heightRef.current = target;
        setHeight(target);
      });
    });

    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [layoutKey]);

  return (
    <div
      className={cn(
        // glass-pill on the shell-chrome element itself — nesting it inside
        // shell-chrome's clip-path would form a backdrop root and kill the
        // glass panel's backdrop-filter, rendering the rail flat.
        "shell-chrome shell-chrome--axis-y glass-pill clay glass-no-hover overflow-hidden rounded-full",
        className,
      )}
      style={{
        height: height ?? undefined,
        transition:
          canTransition && height != null
            ? `height 350ms ${BUBBLE_EASE}`
            : undefined,
      }}
    >
      <nav
        ref={setRefs}
        className="relative flex w-fit min-w-[3.25rem] flex-col items-stretch gap-0.5 p-1.5"
        aria-label="Primary"
        onMouseLeave={onMouseLeave}
      >
        {children}
      </nav>
    </div>
  );
}

function useRailBlob(
  navRef: RefObject<HTMLElement | null>,
  targetId: string | null,
  remeasureKey: string,
) {
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const [blob, setBlob] = useState<BlobRect | null>(null);
  const [visible, setVisible] = useState(false);

  const measure = useCallback(() => {
    const nav = navRef.current;
    if (!nav || !targetId) {
      setVisible(false);
      return;
    }

    const el = itemRefs.current.get(targetId);
    if (!el) return;

    const navRect = nav.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    setBlob({
      top: elRect.top - navRect.top + nav.scrollTop,
      left: elRect.left - navRect.left + nav.scrollLeft,
      width: elRect.width,
      height: elRect.height,
    });
    setVisible(true);
  }, [navRef, targetId]);

  const setItemRef = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) itemRefs.current.set(id, node);
      else itemRefs.current.delete(id);
    },
    [],
  );

  useLayoutEffect(() => {
    measure();
  }, [measure, remeasureKey]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const ro = new ResizeObserver(() => measure());
    ro.observe(nav);
    for (const el of itemRefs.current.values()) {
      ro.observe(el);
    }
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, navRef, remeasureKey]);

  return { blob, visible, setItemRef };
}

export function IconRail({ className }: IconRailProps) {
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { phase } = useAppBootstrap();
  const visibleNavItems = useVisibleNavItems();
  const shellLayoutKey = visibleNavItems.map((i) => i.id).join(",");
  const visibleIdsKey = shellLayoutKey;

  const prevVisibleIdsRef = useRef<string | null>(null);
  const [lateEnterIds, setLateEnterIds] = useState<Set<string>>(() => new Set());

  useLayoutEffect(() => {
    if (phase !== "ready") {
      prevVisibleIdsRef.current = visibleIdsKey;
      return;
    }
    const prev = prevVisibleIdsRef.current;
    prevVisibleIdsRef.current = visibleIdsKey;
    if (prev == null || prev === visibleIdsKey) return;

    const prevSet = new Set(prev.split(",").filter(Boolean));
    const added = visibleIdsKey
      .split(",")
      .filter((id) => id.length > 0 && !prevSet.has(id));
    if (added.length === 0) return;

    setLateEnterIds(new Set(added));
    const frame = requestAnimationFrame(() => setLateEnterIds(new Set()));
    return () => cancelAnimationFrame(frame);
  }, [phase, visibleIdsKey]);

  const activeId = activeNavId(pathname);
  const visibleIdSet = useMemo(
    () => new Set(visibleNavItems.map((item) => item.id)),
    [visibleNavItems],
  );
  const resolvedActiveId = visibleIdSet.has(activeId) ? activeId : null;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const homeQuickLinkPreview =
    pathname === "/dashboard" ||
    pathname === "/preview" ||
    pathname.startsWith("/dashboard/");
  const externalPreview = useNavRailPreviewOptional()?.preview ?? null;
  const externalPreviewId =
    homeQuickLinkPreview &&
    externalPreview != null &&
    visibleIdSet.has(externalPreview.navId)
      ? externalPreview.navId
      : null;
  const effectiveHoveredId =
    hoveredId != null && visibleIdSet.has(hoveredId)
      ? hoveredId
      : externalPreviewId;
  const targetId = effectiveHoveredId ?? resolvedActiveId;
  const isHovering = effectiveHoveredId != null;

  const remeasureKey = `${shellLayoutKey}-${targetId}`;
  const { blob, visible, setItemRef } = useRailBlob(
    navRef,
    targetId,
    remeasureKey,
  );

  const prefetch = useHoverPrefetch();

  return (
    <div className={cn("relative isolate w-fit overflow-visible", className)}>
      <AnimatedIconRailShell
        layoutKey={shellLayoutKey}
        navRef={navRef}
        onMouseLeave={() => setHoveredId(null)}
      >
        <div
          aria-hidden="true"
          className={cn(
            "rail-blob pointer-events-none absolute z-0",
            isHovering && "rail-blob--hover",
            visible ? "opacity-100" : "opacity-0",
          )}
          style={{
            top: blob?.top ?? 0,
            left: blob?.left ?? 0,
            width: blob?.width ?? 52,
            height: blob?.height ?? 56,
          }}
        />

        {visibleNavItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = resolvedActiveId === item.id;
          const isPreviewed = item.id === effectiveHoveredId;
          return (
            <div
              key={item.id}
              ref={setItemRef(item.id)}
              className={cn(
                "rail-nav-item relative z-10 shrink-0",
                isActive && "rail-nav-item--active",
                isPreviewed && "rail-nav-item--preview",
                lateEnterIds.has(item.id) && "rail-nav-item--late-enter",
              )}
              style={
                {
                  "--rail-stagger-index": index,
                } as CSSProperties
              }
              onMouseEnter={() => {
                setHoveredId(item.id);
                prefetch.start(item.path);
              }}
              onMouseLeave={prefetch.cancel}
              onPointerDown={() => prefetch.immediate(item.path)}
              onFocus={() => {
                setHoveredId(item.id);
                prefetch.start(item.path);
              }}
              onBlur={() => {
                setHoveredId(null);
                prefetch.cancel();
              }}
            >
              <NavLink
                to={item.path}
                end={item.path === "/" || item.path === "/schedule"}
                className={({ isActive: linkActive }) =>
                  cn(
                    "rail-nav-item__link",
                    linkActive && "rail-nav-item__link--active",
                    isPreviewed && !linkActive && "rail-nav-item__link--preview",
                  )
                }
              >
                <span className="rail-nav-item__icon" aria-hidden="true">
                  <Icon className="size-[18px]" />
                </span>
                <span className="rail-nav-item__label">{item.label}</span>
              </NavLink>
            </div>
          );
        })}
      </AnimatedIconRailShell>
    </div>
  );
}

export { activeNavId };
